import { AUTH_TOKEN_KEY } from '@/constant/localStorage';
import { attachmentDownloadPath } from '@/utils/bigChangeAttachment';
import storage from '@/utils/storage';
import { uniqueFilename } from '@/utils/uniqueFilename';
import { zipStored } from '@/utils/zipStore';

const saveBlob = (blob: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const attachmentBytes = async (url: string, filename: string) => {
  const token = storage.getItem(AUTH_TOKEN_KEY);
  const response = await fetch(attachmentDownloadPath(url, filename), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const type = response.headers.get('content-type') ?? '';
  if (!response.ok || type.includes('text/html')) {
    throw new Error('Could not save this file');
  }
  return new Uint8Array(await response.arrayBuffer());
};

/** Saves a BigChange file without leaving the current page. */
export async function saveAttachment(url: string, filename: string) {
  const bytes = await attachmentBytes(url, filename);
  const type = filename.toLowerCase().endsWith('.pdf')
    ? 'application/pdf'
    : 'application/octet-stream';
  saveBlob(new Blob([bytes as unknown as BlobPart], { type }), filename);
}

export type SavedArchive = {
  saved: number;
  failed: number;
};

/**
 * One click may start only one download. Later clicks are dropped, so every
 * file goes into a single archive.
 */
export async function saveAttachments(
  files: { url: string; filename: string }[],
  archiveName: string
): Promise<SavedArchive> {
  const settled = await Promise.all(
    files.map(async file => {
      try {
        const bytes = await attachmentBytes(file.url, file.filename);
        return { filename: file.filename, bytes };
      } catch {
        return null;
      }
    })
  );
  const ready = settled.flatMap(file => (file ? [file] : []));
  if (!ready.length) {
    throw new Error('Could not save this file');
  }

  const used = new Map<string, number>();
  saveBlob(
    zipStored(
      ready.map(file => ({
        name: uniqueFilename(file.filename, used),
        bytes: file.bytes,
      }))
    ),
    archiveName
  );

  return { saved: ready.length, failed: files.length - ready.length };
}
